import { useEffect, useState } from 'react';
import { ImagePlus, Globe } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button, Input, Checkbox, Dialog, cn } from '../../ui';
import { createArea, updateArea, subirImagenArea, imagenAreaUrl } from '../api';
import useImagenBlob from '../../../../funciones/useImagenBlob';

const MAX_IMG = 5 * 1024 * 1024;

/**
 * Crear / editar un área SEPE.
 * Fase 3 — UX unificada: la imagen se sube en el MISMO submit. Si hay un archivo
 * pendiente, al pulsar "Guardar" se sube tras crear/editar el área (un solo
 * click). Ya no se reabre el diálogo en edit para subir la imagen.
 *
 * props: { open, modo: 'create'|'edit', area, onClose, onSaved }
 *   - onSaved(area, modo): tras guardar. El padre refresca la lista y cierra.
 */
export default function SepeAreaDialog({ open, modo, area, onClose, onSaved }) {
  const esEdit = modo === 'edit';

  const [nombre, setNombre] = useState('');
  const [publicado, setPublicado] = useState(false);
  const [slug, setSlug] = useState('');
  const [imagen, setImagen] = useState(null);
  const [file, setFile] = useState(null);       // imagen pendiente de subir
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [imgErr, setImgErr] = useState('');

  // (Re)inicializa el formulario cuando se abre o cambia el área/modo.
  useEffect(() => {
    if (!open) return;
    setNombre(area?.nombre || '');
    setPublicado(!!area?.publicado_web);
    setSlug(area?.slug || '');
    setImagen(area?.imagen || null);
    setFile(null);
    setPreview(null);
    setErr('');
    setImgErr('');
  }, [open, modo, area]);

  // Imagen guardada (solo en edit): el hook la descarga con Bearer como ObjectURL.
  const imagenBlobUrl = useImagenBlob(esEdit && imagen ? imagenAreaUrl(imagen) : null);
  const imgActual = preview || imagenBlobUrl;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_IMG) { setImgErr('La imagen supera el máximo de 5 MB.'); return; }
    setImgErr('');
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const guardar = async () => {
    const nom = nombre.trim();
    if (!nom) { setErr('El nombre es obligatorio.'); return; }
    if (nom.length > 120) { setErr('El nombre no puede superar los 120 caracteres.'); return; }
    setSaving(true); setErr('');
    try {
      let saved;
      let areaId;
      if (esEdit) {
        const { data } = await updateArea(area.id, { nombre: nom, publicado_web: publicado });
        saved = data; areaId = area.id;
      } else {
        const { data } = await createArea({ nombre: nom, publicado_web: publicado });
        saved = data; areaId = data.id;
      }

      // Si hay imagen pendiente, se sube tras guardar el área (mismo submit).
      // Si el área falló arriba, no llegamos aquí (no se sube nada).
      if (file && areaId) {
        try {
          await subirImagenArea(areaId, file);
        } catch (imgE) {
          // El área quedó creada/editada; solo falló la imagen → avisar y cerrar.
          toast.error('Área guardada, pero no se pudo subir la imagen. Vuelve a intentarlo abriendo el área.');
          onSaved?.(saved, esEdit ? 'edit' : 'create');
          return;
        }
      }

      toast.success(esEdit ? 'Área guardada' : 'Área creada');
      onSaved?.(saved, esEdit ? 'edit' : 'create');
    } catch (e) {
      setErr(e?.response?.data?.error || 'No se pudo guardar el área.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={esEdit ? 'Editar área' : 'Nueva área'}
      description="Cursos SEPE · áreas"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
          <Input
            value={nombre}
            maxLength={120}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej. Diseño Web"
          />
          <p className="text-xs text-slate-400 mt-1 text-right">{nombre.length}/120</p>
        </div>

        <label className="flex items-center gap-2.5">
          <Checkbox checked={publicado} onChange={(e) => setPublicado(e.target.checked)} />
          <span className="text-sm text-slate-700 flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-emerald-600" /> Publicada en la web pública
          </span>
        </label>

        {esEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Slug</label>
            <Input value={slug} readOnly className="font-mono text-slate-500 bg-slate-50" />
            <p className="text-xs text-slate-400 mt-1">Se genera del nombre. Cambia automáticamente al renombrar.</p>
          </div>
        )}

        {/* Imagen del área — disponible también en create; se sube al Guardar. */}
        <div className="border-t border-slate-200 pt-4">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Imagen del área</h3>
          <div className="flex items-start gap-4">
            <div className="w-32 h-32 shrink-0 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center">
              {imgActual
                ? <img src={imgActual} alt="" className="w-full h-full object-cover" />
                : <span className="text-slate-300 text-xs">Sin imagen</span>}
            </div>
            <div className="flex-1">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50">
                <ImagePlus className="w-4 h-4" />
                {imgActual ? 'Cambiar imagen' : 'Elegir imagen'}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
              </label>
              <p className="text-xs text-slate-400 mt-2">JPG, PNG o WebP. Máx. 5 MB. Se convierte a WebP.</p>
              {file && (
                <p className="text-xs text-amber-600 mt-2">Imagen pendiente de guardar. Pulsa «Guardar» para subirla.</p>
              )}
              {imgErr && <p className="text-sm text-rose-600 mt-2">{imgErr}</p>}
            </div>
          </div>
        </div>

        {err && <p className={cn('text-sm text-rose-600')}>{err}</p>}
      </div>
    </Dialog>
  );
}
